<script setup lang="ts">
import { Badge } from "@/components/ui/badge";
import DynamicBackground from "@/components/animations/DynamicBackground.vue";
import AnimatedText from "@/components/animations/AnimatedText.vue";
import AnimatedButton from "@/components/animations/AnimatedButton.vue";
import FloatingElements from "@/components/animations/FloatingElements.vue";

// 平滑滚动到指定区域
const scrollToServices = () => {
  const element = document.getElementById('services');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};

const scrollToContact = () => {
  const element = document.getElementById('contact');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};
</script>

<template>
  <section class="hero-section">
    <!-- 动态背景层 -->
    <DynamicBackground variant="hero" :show-particles="true" :show-streaming-light="true" />

    <div class="hero-container">
      <div class="hero-content">
        <Badge variant="outline" class="hero-badge group">
          <span class="hero-badge-highlight">
            <Badge class="hero-badge-smart">智能</Badge>
          </span>
          <span class="hero-badge-text"> 物联网 + AI 解决方案领先企业 </span>
        </Badge>

        <AnimatedText text="创诚纪元人工智能 物联网+AI智能解决方案专家" tag="h1" text-class="hero-title group" animation-type="slide-left"
          delay="0.2s" :gradient-text="true" gradient-class="hero-gradient" :break-words="true" :break-after="[2, 5]"
          :show-cursor="true" :glow-effect="true" />

        <p class="hero-description" style="animation-delay: 0.4s">
          专注于<span class="hero-highlight">人工智能</span>与<span class="hero-highlight">物联网技术</span>融合，提供工业自动化、智能仪器仪表、
          环境监测等全方位解决方案，赋能传统产业数字化转型升级。
        </p>

        <div class="hero-buttons" style="animation-delay: 0.6s">
          <AnimatedButton variant="primary" :show-arrow="true" :effects="['scale', 'shadow']"
            button-class="hero-primary-btn" @click="scrollToServices">
            了解产品
          </AnimatedButton>

          <AnimatedButton variant="secondary" :effects="['scale', 'shadow']" button-class="hero-secondary-btn"
            @click="scrollToContact">
            合作咨询
          </AnimatedButton>
        </div>
      </div>

      <FloatingElements variant="hero-image" class="mt-14" />
    </div>
  </section>
</template>

<style scoped>
/* Hero 主要样式 */
.hero-section {
  @apply container relative overflow-hidden;
}

.hero-container {
  @apply grid place-items-center lg:max-w-screen-xl gap-8 mx-auto py-20 md:py-32 relative z-10;
}

.hero-content {
  @apply text-center space-y-8;
}

/* Badge 样式 */
.hero-badge {
  @apply text-sm py-2 hover:scale-105 transition-all duration-300 cursor-pointer;
  animation: fadeInUp 0.6s ease-out;
}

.hero-badge-highlight {
  @apply mr-2 text-primary relative;
}

.hero-badge-smart {
  @apply animate-pulse-glow group-hover:animate-bounce;
}

.hero-badge-text {
  @apply group-hover:text-primary transition-colors duration-300;
}

/* 标题样式 */
.hero-title {
  @apply max-w-screen-md mx-auto text-center text-5xl md:text-6xl font-bold relative;
}

.hero-gradient {
  @apply from-[#D247BF] to-primary hover:from-primary hover:to-[#D247BF];
}

/* 描述文本样式 */
.hero-description {
  @apply max-w-screen-sm mx-auto text-xl text-muted-foreground leading-relaxed;
  animation: fadeInUp 0.8s ease-out;
}

.hero-highlight {
  @apply text-primary font-semibold hover:scale-105 inline-block transition-transform duration-300 cursor-pointer;
}

/* 按钮容器样式 */
.hero-buttons {
  @apply space-y-4 md:space-y-0 md:space-x-4;
  animation: fadeInUp 1s ease-out;
}

.hero-primary-btn {
  @apply w-5/6 md:w-1/4 font-bold;
}

.hero-secondary-btn {
  @apply w-5/6 md:w-1/4 font-bold;
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseGlow {

  0%,
  100% {
    box-shadow: 0 0 5px hsl(var(--primary) / 0.5);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 0 20px hsl(var(--primary) / 0.8), 0 0 30px hsl(var(--primary) / 0.4);
    transform: scale(1.05);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.8s ease-out forwards;
  opacity: 0;
}

.animate-pulse-glow {
  animation: pulseGlow 2s ease-in-out infinite;
}
</style>
